import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Spinner from "../../componentLibrary/Spinner";
import { usePermissions } from "../../contexts/PermissionContext";
import { useFeatureFlag } from "../../contexts/FeatureFlagContext";
import { FeatureFlag } from "../../constants/featureFlags";
import { useGetTopic, useGetRecaps, useGetResponses } from "../../api/queries";
import { LessonI, ModuleStatus, ResponseEnum } from "../../types";
import { useCreateOrGetRecap } from "../../api/mutations";
import { useNotification } from "../../hooks/useNotification";
import { ErrorDisplay } from "../../utils/ErrorHandling";
import TopicTopNavigation from "../../components/TopicTopNavigation";
import { useHasToolboxContent } from "../../hooks/useHasToolboxContent";
import GoToToolboxButton from "../../components/GoToToolboxButton";
import TopicSideNav from "./components/TopicSideNav";
import LessonList from "./LessonList";
import TopicHeader from "./TopicHeader";
import TopicTrailer from "./TopicTrailer";

const TopicPage = () => {
  const { moduleId } = useParams();
  const { canReadContent, loading: permsLoading } = usePermissions();
  const { notify } = useNotification();
  const topicTocEnabled = useFeatureFlag(FeatureFlag.TOPIC_TOC);

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const mutation = useCreateOrGetRecap({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recaps"] });
    },
    onError: async () => {
      notify("error", "There was an error.");
    },
  });

  const { data: module, isError, isLoading, error } = useGetTopic(moduleId);
  const { data: recaps } = useGetRecaps();
  const { data: responses } = useGetResponses();
  const { hasContent: hasToolboxContent } = useHasToolboxContent(moduleId);

  if (isLoading || permsLoading) return <Spinner className="mt-4 mx-auto" />;
  if (isError || !module)
    return <ErrorDisplay error={error} messageSource="api" />;

  if (!canReadContent && module.status === ModuleStatus.Coming_Soon) {
    return <Navigate to="/explore" replace />;
  }

  const getLessonProgress = (lessonId: string, totalPages: number) => {
    if (!recaps || !responses) return 0;

    const lesson = module.lessons.find((l) => l._id === lessonId);

    const recap = recaps.find((recap) => {
      const recapLessonId =
        typeof recap.lesson === "string" ? recap.lesson : recap.lesson._id;
      const recapModuleId =
        typeof recap.module === "string" ? recap.module : recap.module._id;
      return recapLessonId === lessonId && recapModuleId === moduleId;
    });

    if (recap?.completed) return 100;

    const hasSectionRecapResponse = responses.some(
      (response) =>
        response.type === ResponseEnum.SECTION_RECAP &&
        response.title === lesson?.title,
    );

    if (hasSectionRecapResponse && !recap) return 100;
    if (totalPages === 0) return 0;

    return Math.min(
      ((recap?.pages_visited?.length ?? 0) / totalPages) * 100,
      100,
    );
  };

  const onClickLesson = (lessonId: string) => {
    const lesson = module.lessons.find((l) => l._id === lessonId);
    if (!lesson) return;

    mutation.mutate({ lesson: lessonId, module: module._id });
    const recap = recaps?.find((recap) => {
      const recapLessonId =
        typeof recap.lesson === "string" ? recap.lesson : recap.lesson._id;
      const recapModuleId =
        typeof recap.module === "string" ? recap.module : recap.module._id;
      return recapLessonId === lessonId && recapModuleId === moduleId;
    });
    navigate(
      `/topics/${module.slug ?? moduleId}/sections/${lesson.slug ?? lessonId}`,
      {
        state: {
          initialPage: Math.max(0, (recap?.pages_visited?.length ?? 0) - 1),
          referringPageName: module.title,
        },
      },
    );
  };

  const handleSideNavLessonSelect = (
    lessonId: string,
    pageIndex: number = 0,
  ) => {
    const lesson = module.lessons.find((l) => l._id === lessonId);
    if (!lesson) return;
    mutation.mutate({ lesson: lessonId, module: module._id });
    navigate(
      `/topics/${module.slug ?? moduleId}/sections/${lesson.slug ?? lessonId}`,
      {
        state: { initialPage: pageIndex, referringPageName: module.title },
      },
    );
  };

  const onToolboxClick = () =>
    navigate(`/my-toolboxes/${module._id}`, {
      state: { from: `/topics/${module.slug ?? moduleId}` },
    });

  const firstLesson =
    module.lessons.length === 0 ? undefined : module.lessons[0];

  const mostRecentRecap = recaps
    ?.filter((recap) => {
      const recapModuleId =
        typeof recap.module === "string" ? recap.module : recap.module._id;
      return recapModuleId === module._id;
    })
    ?.sort((a, b) => {
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bTime - aTime;
    })[0];

  const mostRecentLesson: LessonI | undefined = mostRecentRecap
    ? typeof mostRecentRecap.lesson === "string"
      ? module.lessons.find((l) => l._id === mostRecentRecap.lesson)
      : (mostRecentRecap.lesson as LessonI)
    : undefined;

  const continueLesson =
    mostRecentLesson?._id && mostRecentLesson._id !== firstLesson?._id
      ? mostRecentLesson
      : undefined;

  const header = (
    <TopicHeader
      icon={
        typeof module.icon !== "string" ? (
          <img src={module.icon.src} className="w-10 h-10" alt="Module Icon" />
        ) : null
      }
      title={module.title}
      descriptionHtml={module.description}
    />
  );

  return (
    <>
      {/* Top navigation — sticky on mobile, hidden on lg when TOC is active */}
      <div
        className={`sticky top-0 inset-x-0 z-10${
          topicTocEnabled ? " lg:hidden" : ""
        }`}
      >
        <TopicTopNavigation
          module={module}
          onExploreClick={() => navigate("/explore")}
        />
      </div>

      {/* Main content area — single column on mobile, two-column row on lg */}
      <div className="w-full max-w-6xl mx-auto px-4 pt-4 pb-40 flex flex-col gap-8 lg:flex-row lg:px-8 lg:py-8 lg:pb-8">
        {/* Primary content (trailer + header + CTAs) */}
        <div className="w-full min-w-0 flex flex-col items-center text-center lg:flex-1 lg:items-start lg:text-left lg:order-2">
          <TopicTrailer
            header={header}
            trailerVideoUrl={module.trailerVideoUrl}
            trailerTranscript={module.trailerTranscript}
            topicTocEnabled={topicTocEnabled}
            firstLesson={firstLesson}
            continueLesson={continueLesson}
            onClickLesson={onClickLesson}
          />
        </div>

        {/* Sidebar (lesson list / TOC) */}
        <div className="w-full flex flex-col gap-4 lg:w-80 lg:shrink-0 lg:order-1">
          {hasToolboxContent && (
            <div className="hidden lg:block">
              <GoToToolboxButton onClick={onToolboxClick} className="w-full" />
            </div>
          )}
          {topicTocEnabled ? (
            <>
              <TopicSideNav
                module={module}
                onLessonSelect={handleSideNavLessonSelect}
              />
              <div className="lg:hidden">
                <LessonList
                  lessons={module.lessons}
                  moduleColor={module.color}
                  getLessonProgress={getLessonProgress}
                  onClickLesson={onClickLesson}
                />
              </div>
            </>
          ) : (
            <LessonList
              lessons={module.lessons}
              moduleColor={module.color}
              getLessonProgress={getLessonProgress}
              onClickLesson={onClickLesson}
            />
          )}
        </div>
      </div>

      {/* Fixed bottom toolbox button — mobile only */}
      {hasToolboxContent && (
        <div className="fixed inset-x-0 bottom-7 z-10 p-4 pt-3 bg-white border-t border-gray-200 lg:hidden">
          <GoToToolboxButton onClick={onToolboxClick} className="w-full" />
        </div>
      )}
    </>
  );
};

export default TopicPage;
